/**
 * BusRosterList (WoodmereTransportationList.js)
 *
 * Bus/transport roster for the currently active session at the selected site.
 * Site is determined by the chrome site picker (X-Site-Id header sent by axios).
 * Works for 14th Ave, 55th Ave, Woodmere, Rockland, or any site.
 *
 * API: GET /sessions/current-session/         → active session for site
 *      GET /sessions/students/?session=<id>   → students in that session
 *      GET /sessions/students/<pk>/bus-info/
 *      PATCH /sessions/students/<pk>/bus-info/
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Button,
  Alert,
} from '@mui/material';
import { Edit, DirectionsBus } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { sessionAPI, studentAPI, busInfoAPI } from '../../services/api';
import AppHeader from '../AppHeader';

// ---- transport mode config -----------------------------------------------
const TRANSPORT_MODE_CHOICES = [
  { value: '', label: '— Unknown —' },
  { value: 'bus', label: 'Bus' },
  { value: 'parent', label: 'Parent Pickup' },
  { value: 'bus_and_parent', label: 'Bus & Parent' },
  { value: 'other', label: 'Other' },
];

const MODE_CHIP_COLOR = {
  bus: 'primary',
  parent: 'success',
  bus_and_parent: 'secondary',
  other: 'default',
};

const ModeChip = ({ mode }) => {
  if (!mode) return <Chip label="Unknown" size="small" />;
  const choice = TRANSPORT_MODE_CHOICES.find((c) => c.value === mode);
  return (
    <Chip
      label={choice ? choice.label : mode}
      color={MODE_CHIP_COLOR[mode] || 'default'}
      size="small"
    />
  );
};

// ---- empty bus info shape -------------------------------------------------
const EMPTY_BUS_INFO = {
  transport_mode: '',
  pickup_address: '',
  dropoff_address: '',
  pickup_phone: '',
  dropoff_phone: '',
  notes: '',
};

// ==========================================================================
const WoodmereTransportationList = () => {
  useAuth();

  const [currentSession, setCurrentSession] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [search, setSearch] = useState('');

  // Edit modal state
  const [editStudent, setEditStudent] = useState(null);
  const [editDraft, setEditDraft] = useState(EMPTY_BUS_INFO);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // ------------------------------------------------------------------
  // Load active session then students — site comes from X-Site-Id
  // header automatically injected by the axios interceptor.
  // ------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setFetchError('');
      setStudents([]);
      setCurrentSession(null);
      try {
        const sessionRes = await sessionAPI.getCurrentSession();
        const session = sessionRes.data;
        if (cancelled) return;
        if (!session || !session.id) {
          setFetchError('No active session found for this site.');
          setLoading(false);
          return;
        }
        setCurrentSession(session);
        const studRes = await studentAPI.getStudents(session.id, { page_size: 2000 });
        if (cancelled) return;
        const list = studRes.data.results || studRes.data || [];
        const arr = Array.isArray(list) ? list : [];
        arr.sort((a, b) =>
          (a.last_name || '').localeCompare(b.last_name || '') ||
          (a.first_name || '').localeCompare(b.first_name || '')
        );
        setStudents(arr);
      } catch (err) {
        if (!cancelled) setFetchError('Failed to load data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // ------------------------------------------------------------------
  // Search filter
  // ------------------------------------------------------------------
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) =>
      `${s.first_name} ${s.last_name}`.toLowerCase().includes(q) ||
      (s.class_num || '').toLowerCase().includes(q)
    );
  }, [students, search]);

  // ------------------------------------------------------------------
  // Open edit modal
  // ------------------------------------------------------------------
  const handleEdit = useCallback(async (student) => {
    setEditStudent(student);
    setSaveError('');
    if (student.bus_info) {
      setEditDraft({ ...EMPTY_BUS_INFO, ...student.bus_info });
    } else {
      try {
        const res = await busInfoAPI.get(student.id);
        setEditDraft({ ...EMPTY_BUS_INFO, ...res.data });
      } catch (_) {
        setEditDraft(EMPTY_BUS_INFO);
      }
    }
  }, []);

  const handleCloseEdit = () => {
    setEditStudent(null);
    setEditDraft(EMPTY_BUS_INFO);
    setSaveError('');
  };

  const handleSave = async () => {
    if (!editStudent) return;
    setSaving(true);
    setSaveError('');
    try {
      const res = await busInfoAPI.update(editStudent.id, editDraft);
      setStudents((prev) =>
        prev.map((s) =>
          s.id === editStudent.id ? { ...s, bus_info: res.data } : s
        )
      );
      handleCloseEdit();
    } catch (err) {
      setSaveError('Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getBusInfo = (student) => student.bus_info || {};

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <>
      <AppHeader title="Bus Roster" icon={<DirectionsBus />} />
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          {currentSession && (
            <Typography variant="body2" color="text.secondary">
              {currentSession.name || currentSession.display_name}
            </Typography>
          )}

          <TextField
            size="small"
            placeholder="Search by name or class…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 240 }}
          />

          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {filtered.length} student{filtered.length !== 1 ? 's' : ''}
          </Typography>
        </Box>

        {fetchError && <Alert severity="error" sx={{ mb: 2 }}>{fetchError}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={2}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Student</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Class</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Transport Mode</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Pickup Address</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Dropoff Address</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Pickup Phone</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Dropoff Phone</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {students.length === 0 ? 'No students in this session.' : 'No results match your search.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((student) => {
                    const bi = getBusInfo(student);
                    return (
                      <TableRow key={student.id} hover>
                        <TableCell>
                          {student.last_name}, {student.first_name}
                        </TableCell>
                        <TableCell>{student.class_num || '—'}</TableCell>
                        <TableCell>
                          <ModeChip mode={bi.transport_mode} />
                        </TableCell>
                        <TableCell>{bi.pickup_address || '—'}</TableCell>
                        <TableCell>{bi.dropoff_address || '—'}</TableCell>
                        <TableCell>{bi.pickup_phone || '—'}</TableCell>
                        <TableCell>{bi.dropoff_phone || '—'}</TableCell>
                        <TableCell>
                          <Tooltip title="Edit bus info">
                            <IconButton size="small" onClick={() => handleEdit(student)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>

      {/* ---- Edit modal ---- */}
      <Dialog open={Boolean(editStudent)} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>
          Bus Info — {editStudent ? `${editStudent.first_name} ${editStudent.last_name}` : ''}
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Transport Mode</InputLabel>
            <Select
              value={editDraft.transport_mode}
              label="Transport Mode"
              onChange={(e) => setEditDraft((d) => ({ ...d, transport_mode: e.target.value }))}
            >
              {TRANSPORT_MODE_CHOICES.map((c) => (
                <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Pickup Address"
            size="small"
            fullWidth
            value={editDraft.pickup_address}
            onChange={(e) => setEditDraft((d) => ({ ...d, pickup_address: e.target.value }))}
          />
          <TextField
            label="Pickup Phone"
            size="small"
            fullWidth
            value={editDraft.pickup_phone}
            onChange={(e) => setEditDraft((d) => ({ ...d, pickup_phone: e.target.value }))}
          />
          <TextField
            label="Dropoff Address"
            size="small"
            fullWidth
            value={editDraft.dropoff_address}
            onChange={(e) => setEditDraft((d) => ({ ...d, dropoff_address: e.target.value }))}
          />
          <TextField
            label="Dropoff Phone"
            size="small"
            fullWidth
            value={editDraft.dropoff_phone}
            onChange={(e) => setEditDraft((d) => ({ ...d, dropoff_phone: e.target.value }))}
          />
          <TextField
            label="Notes"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={editDraft.notes}
            onChange={(e) => setEditDraft((d) => ({ ...d, notes: e.target.value }))}
          />

          {saveError && <Alert severity="error">{saveError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WoodmereTransportationList;
